import { C, colHead } from '../theme'
import { bps, usd } from '../lib/format'
import { Note, Px, SectionHead, Strong } from './ui/primitives'
import { LIMITER_LABEL } from '../data/mock/constants'
import { crossHeadroomBps, pairProduct, parseDirection } from '../lib/orientation'
import type { DirectionLevels, Snapshot } from '../types'

const ROW = '1.4fr 1fr 1fr 1fr'

/** Обратная нога той же пары: base и quote меняются местами. */
function reverseOf(all: DirectionLevels[], d: DirectionLevels): DirectionLevels | undefined {
  const { chainId, base, quote } = parseDirection(d.key)
  return all.find((o) => {
    const p = parseDirection(o.key)
    return p.chainId === chainId && p.base === quote && p.quote === base
  })
}

export function Directions({
  snap,
  openKey,
  onOpen,
  showNotes,
}: {
  snap: Snapshot
  openKey: string | null
  onOpen: (k: string | null) => void
  showNotes: boolean
}) {
  return (
    <>
      <SectionHead
        title="Live quotes"
        right={
          <span
            style={{
              fontSize: 14,
              letterSpacing: '.18em',
              textTransform: 'uppercase',
              color: C.muted,
            }}
          >
            {snap.directions.length} directions · click a row for its ladder
          </span>
        }
      />

      <div
        data-dirrow
        style={{
          ...colHead,
          display: 'grid',
          gridTemplateColumns: ROW,
          gap: 16,
          padding: '12px 0 8px',
          fontSize: 13,
          letterSpacing: '.14em',
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <span>Direction</span>
        <span style={{ textAlign: 'right' }}>Published</span>
        <span data-col3 style={{ textAlign: 'right' }}>
          Price
        </span>
        <span data-col4 style={{ textAlign: 'right' }}>
          Age
        </span>
      </div>

      {snap.directions.map((d) => {
        const open = openKey === d.key
        const withdrawn = d.status === 'withdrawn_local_venue_live'
        const atCap = d.projectedLtvPct != null && d.projectedLtvPct > snap.derived.ownCapPct - 0.01
        const deltaBps = (d.price / d.firmPrice - 1) * 10_000

        const rev = reverseOf(snap.directions, d)
        const product = rev ? pairProduct(d.price, rev.price) : null
        const crossBps = product == null ? null : crossHeadroomBps(product)

        return (
          <div key={d.key}>
            <div
              data-dirrow
              onClick={() => onOpen(open ? null : d.key)}
              style={{
                display: 'grid',
                gridTemplateColumns: ROW,
                gap: 16,
                alignItems: 'baseline',
                padding: '14px 0',
                borderBottom: `1px solid ${C.rule}`,
                cursor: 'pointer',
                background: open ? C.rowHover : 'transparent',
              }}
            >
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: withdrawn ? C.muted : C.text,
                }}
              >
                {d.label}
                <span style={{ fontSize: 13, fontWeight: 400, color: C.muted, paddingLeft: 9 }}>
                  {withdrawn ? 'withdrawn' : atCap ? 'at cap' : ''}
                </span>
              </span>
              <span style={{ fontSize: 20, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {usd(d.sumUsd)}
              </span>
              <span
                data-col3
                style={{ fontSize: 17, textAlign: 'right', color: C.muted2, whiteSpace: 'nowrap' }}
              >
                <Px value={d.price} tailColor={C.faint} />
              </span>
              <span
                data-col4
                style={{
                  fontSize: 15,
                  textAlign: 'right',
                  color: d.ageSec > 15 ? C.neg : C.muted,
                  whiteSpace: 'nowrap',
                }}
              >
                {d.ageSec.toFixed(1)} s
              </span>
            </div>

            {open && (
              <div
                data-two
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 34,
                  background: C.panel,
                  border: `1px solid ${C.border}`,
                  borderTop: 'none',
                  padding: '16px 18px 18px',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={colHead}>
                    Ladder · {d.rungs.length} {d.rungs.length === 1 ? 'rung' : 'rungs'}, {d.mode}
                  </div>
                  {d.rungs.map((g, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '80px minmax(0,1fr) auto',
                        gap: 12,
                        alignItems: 'baseline',
                        padding: '9px 0',
                        borderBottom: `1px solid ${C.rule}`,
                        fontSize: 15,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ color: C.muted2 }}>rung {i + 1}</span>
                      <span style={{ textAlign: 'right', color: C.muted2 }}>
                        <Px value={g.price} tailColor={C.faint} />
                      </span>
                      <span style={{ textAlign: 'right', fontWeight: 600 }}>
                        {usd(g.notionalUsd)}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px minmax(0,1fr) auto',
                      gap: 12,
                      alignItems: 'baseline',
                      padding: '9px 0',
                      borderBottom: `1px solid ${C.text}`,
                      fontSize: 15,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>sum</span>
                    <span />
                    <span style={{ textAlign: 'right', fontWeight: 700 }}>{usd(d.sumUsd)}</span>
                  </div>
                  <div style={{ fontSize: 14, color: C.muted2, marginTop: 10 }}>
                    The venue routes a ticket up to the <Strong>sum</Strong> of the rungs, not the
                    top one — top rung is {usd(d.topRungUsd)}.
                  </div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={colHead}>Against our own firm quote</div>
                  {[
                    { l: 'advertised', v: <Px value={d.price} />, c: C.text, fw: 400 },
                    { l: 'firm, same size', v: <Px value={d.firmPrice} />, c: C.text, fw: 400 },
                    {
                      l: 'delta',
                      v: bps(deltaBps),
                      c: deltaBps >= 0 ? C.pos : C.neg,
                      fw: 600,
                    },
                    {
                      l: 'settleable now',
                      v: usd(d.settleableUsd),
                      c: d.settleableUsd < d.sumUsd ? C.warn : C.text,
                      fw: 400,
                    },
                    {
                      l: 'projected LTV',
                      v: d.projectedLtvPct == null ? 'not binding' : `${d.projectedLtvPct.toFixed(4)}%`,
                      c: atCap ? C.warn : C.text,
                      fw: 400,
                    },
                    {
                      l: 'limiter',
                      v: LIMITER_LABEL[d.limiter],
                      c: d.limiter === 'unknown' ? C.warn : C.text,
                      fw: 400,
                    },
                    {
                      l: product == null ? 'pair product' : `pair product ×${rev!.label}`,
                      v:
                        product == null
                          ? '—'
                          : `${product.toFixed(6)} · ${crossBps!.toFixed(2)} bps to crossing`,
                      c: product != null && product > 1 ? C.neg : C.text,
                      fw: 600,
                    },
                    {
                      l: 'status',
                      v: withdrawn
                        ? `withdrawn, venue copy ${Math.round(d.venueCopyExpiresInSec ?? 0)} s left`
                        : atCap
                          ? 'at cap — normal'
                          : 'published',
                      c: withdrawn || atCap ? C.warn : C.pos,
                      fw: 600,
                    },
                  ].map((f, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0,1fr) auto',
                        gap: 12,
                        alignItems: 'baseline',
                        padding: '9px 0',
                        borderBottom: `1px solid ${C.rule}`,
                        fontSize: 15,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ color: C.muted2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.l}
                      </span>
                      <span style={{ textAlign: 'right', color: f.c, fontWeight: f.fw }}>{f.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}

      {showNotes && (
        <Note>
          All six directions draw on the <Strong>same LTV headroom</Strong>, so the published sizes
          compete with each other: three slots are taken, and a fourth $400 quote would project past
          our own {snap.derived.ownCapPct}% cap and come back as funding unavailable. A withdrawn
          row is not gone — the venue keeps its copy of the levels for up to 30 seconds, and there
          is no withdraw message in the protocol to take them back sooner. When a pair's product
          goes above 1.0 the book is crossed and the venue cuts the <Strong>pair</Strong>, not one
          side of it.
        </Note>
      )}
    </>
  )
}
